import {
  getCompetitorBatchMobileStoreStats,
  getCompetitorBatchMobileStoreTimeline,
  getCompetitorBatchPcConsoleStoreStats,
  getCompetitorBatchPcConsoleStoreTimeline,
} from '@/pages/opinion/services/competitor';
import {
  arrayToSimpleObj,
  getPercentageValue,
  sortTimeListFunc,
  supplementSingleData,
} from '@/pages/opinion/utils';
import { formatDateFunc } from '@/pages/opinion/utils/time';
import { useState } from 'react';

export default function useGameStoreRequest({ xAxisList }: any) {
  const [selectedStacked, setSelectedStacked] = useState<Array<any>>([]);
  const [lifetimeStacked, setLifetimeStacked] = useState<Array<any>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const requestMobileBar = (params: any) => {
    return getCompetitorBatchMobileStoreStats({
      ...params,
      types: ['lifetime', 'selected'],
    }).then((data: any) => {
      let starKeys = [
        { detailKey: 1, key: 'OneStartCount' },
        { detailKey: 2, key: 'TwoStartCount' },
        { detailKey: 3, key: 'ThreeStartCount' },
        { detailKey: 4, key: 'FourStartCount' },
        { detailKey: 5, key: 'FiveStartCount' },
        { detailKey: 6, key: 'Score' },
      ];
      const Resp: any = {};
      data = data || [];

      data.forEach(({ game, value }: any) => {
        value?.forEach(({ new_list, type }: any) => {
          if (!Resp[type]) {
            Resp[type] = {};
          }
          const gameDetail =
            xAxisList?.find(
              (v: any) => game.edition_unified_id === v.edition_unified_id,
            ) || {};
          let { score, detail, mentions } = new_list?.[0] || {};

          starKeys.forEach(({ key, detailKey }) => {
            if (!Resp[type][key]) {
              Resp[type][key] = {
                name: key,
                games: [],
              };
            }
            let value = score || 0;
            let rate = 0;
            // 6 代表的是score，不在detail内
            if (detailKey !== 6) {
              value = detail?.[detailKey] || 0;
              rate = getPercentageValue(value, mentions || 0, 8);
            }
            Resp[type][key].games.push({
              ...game,
              value: value,
              rate: rate,
              name: gameDetail.entity_name,
              cover: gameDetail.cover,
            });
          });
        });
      });
      const sortStarKeys = arrayToSimpleObj(starKeys, 'detailKey', 'key');
      const sortFunc = (a: any, b: any) => {
        return sortStarKeys[a.name] - sortStarKeys[b.name];
      };

      let lifetimeData: Array<any> = Object.values(Resp?.lifetime || {}).sort(
        sortFunc,
      );
      let selectedData: Array<any> = Object.values(Resp?.selected || {}).sort(
        sortFunc,
      );
      setLifetimeStacked(lifetimeData);
      setSelectedStacked(selectedData);
      setIsLoading(false);
    });
  };

  const requestPcConsoleBar = (params: any, platform: any) => {
    return getCompetitorBatchPcConsoleStoreStats(params).then((data: any) => {
      data = data || [];
      let user_score_response = data.map(({ game, value }: any) => {
        const { list } = value?.[0] || { list: [] };
        const userScore = list?.find((item: any) => item.key === 'user_score');
        const gameDetail =
          xAxisList?.find(
            (v: any) => game.edition_unified_id === v.edition_unified_id,
          ) || {};
        return {
          ...game,
          name: gameDetail.entity_name,
          cover: gameDetail.cover,
          user_score: {
            Review: userScore?.review || 0,
            Score: userScore?.score || 0,
            Name: '',
          },
        };
      });
      setLifetimeStacked(user_score_response || []);
      setSelectedStacked([]);
      setIsLoading(false);
    });
  };

  const separateGames = (params: any) => {
    let gameList = params.games;
    let pcGames: Array<any> = [],
      mobileGames: Array<any> = [],
      consoleGames: Array<any> = [];

    gameList.forEach((game: any) => {
      if (game.entity_type === 'mobile') {
        mobileGames.push(game);
      } else if (game.entity_type === 'pc') {
        pcGames.push(game);
      } else {
        consoleGames.push(game);
      }
    });

    return [pcGames, mobileGames, consoleGames];
  };

  const requestGameStoreBar = async (
    params: any,
    channel: any,
    platform: any,
    metacrtic: any,
  ) => {
    let [pcGames, mobileGames, consoleGames] = separateGames(params);
    setIsError(false);
    setIsLoading(true);

    if (channel === 'metacritic') {
      const isPc = platform === 'pc';
      await requestPcConsoleBar(
        {
          ...params,
          channels: [channel],
          games: isPc ? pcGames : consoleGames,
          device: isPc ? '' : metacrtic,
        },
        platform,
      );
    } else {
      await requestMobileBar({
        ...params,
        channel: [channel],
        games: mobileGames,
      });
    }
  };

  const [isTimelineLoading, setIsTimelineLoading] = useState<boolean>(false);
  const [isTimelineError, setIsTimelineError] = useState<boolean>(false);
  const [selectedTrend, setSelectedTrend] = useState<Array<any>>([]); // 左侧折线图
  const [lifetimeTrend, setLifetimeTrend] = useState<Array<any>>([]); // 右侧折线图

  const requestPcConsoleTimeline = (params: any) => {
    return getCompetitorBatchPcConsoleStoreTimeline(params).then(
      (data: any) => {
        data = data || [];
        let lifetime: Array<any> = [];
        data.forEach(({ game, value }: any) => {
          let { list, timeline_of_start_time, timeline_of_end_time } = value;
          const formatTimeFunc = formatDateFunc(params.date_type);
          list.forEach((item: any) => {
            const realList = supplementSingleData(
              timeline_of_start_time,
              timeline_of_end_time,
              item.timeline?.filter((item: any) => item.key === 'user_score'),
              params.date_type,
              ['user_score'],
            );

            realList.forEach((item) => {
              lifetime.push({
                value: item.score || item.count,
                time: formatTimeFunc(item.time),
                ...game,
              });
            });
          });
        });
        lifetime.sort(sortTimeListFunc('time'));
        setLifetimeTrend(lifetime);
        setIsTimelineLoading(false);
        setSelectedTrend([]);
      },
    );
  };
  const requestMobileTimeline = (params: any) => {
    return getCompetitorBatchMobileStoreTimeline(params).then((data: any) => {
      const dateType = params.date_type;
      data = data || [];
      let ReqMap: any = {
        lifetime: [],
        selected: [],
      };
      data.forEach(({ game, value }: any) => {
        let { timeline_list, timeline_of_start_time, timeline_of_end_time } =
          value;

        timeline_list.forEach(({ list, type }: any) => {
          // 添加一个元素，让后面补齐能有一个基础
          list = (list || [{ channel: params.channel?.[0] || '' }]).map(
            (item: any) => ({ ...item, name: item.channel }),
          );
          const realList = supplementSingleData(
            timeline_of_start_time,
            timeline_of_end_time,
            list,
            dateType,
          );

          realList.forEach((item) => {
            ReqMap[type].push({
              ...item,
              time: formatDateFunc(dateType)(item.time),
              ...game,
              value: item.score || item.count,
            });
          });
        });
      });
      let selectedList = ReqMap.selected.sort(sortTimeListFunc('time'));
      let lifetimeList = ReqMap.lifetime.sort(sortTimeListFunc('time'));
      setSelectedTrend(selectedList);
      setLifetimeTrend(lifetimeList);
      setIsTimelineLoading(false);
    });
  };

  const requestGameStoreTimeline = async (
    params: any,
    channel: any,
    platform: any,
    metacrtic: any,
  ) => {
    let [pcGames, mobileGames, consoleGames] = separateGames(params);

    setIsTimelineError(false);
    setIsTimelineLoading(true);

    if (channel === 'metacritic') {
      const isPc = platform === 'pc';
      await requestPcConsoleTimeline({
        ...params,
        channels: [channel],
        games: isPc ? pcGames : consoleGames,
        device: isPc ? '' : metacrtic,
      });
    } else if (channel) {
      await requestMobileTimeline({
        ...params,
        types: ['selected', 'lifetime'],
        channel: [channel],
        games: mobileGames,
      });
    }
  };

  return {
    isLoading,
    isError,
    requestGameStoreBar,
    selectedStacked,
    lifetimeStacked,
    requestGameStoreTimeline,
    isTimelineLoading,
    isTimelineError,
    lifetimeTrend,
    selectedTrend,
  };
}
